/* ----------------- React --------------- */
import { useEffect, useMemo, useRef, useState } from "react";

/* ----------------- React Native --------------- */
import type { ScrollView } from "react-native";

import type { QuickActionsChipType } from "components/QuickActionChips/types";
/* ----------------- Types --------------- */
import type {
	HeaderSectionData,
	HomeSection,
	SectionData,
	TaskCardProps,
} from "../../types/";

import { useExamContext } from "@features/exam";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
/* ----------------- Hooks --------------- */
import { useDevice } from "@hooks/useDevice";

/* ----------------- UI Components --------------- */
import { Box, ScrollContainer } from "@memorang/ui";
import { ExamsFilterDialog } from "components/ExamsFilterDialog";

import ExamInfoSection from "./ExamInfoSection";
/* ----------------- Child Components --------------- */
import { ModuleRecommendationsSection } from "./ModuleRecommendationsSection";
import ProductsSection from "./ProductsSection";
import { SectionHeader } from "./SectionHeader";
import TasksSection from "./TasksSection";
import TopicsSection from "./TopicsSection";

/* ----------------- Constants and Helpers --------------- */
import { getShowStudyPackViews } from "@helpers/exams";
import type { PracticeTestExam } from "@memorang/types";
import type { DiagnosticCardProps } from "types/tag";
import {
	type ModuleViewMode,
	ModuleViewModeToggle,
} from "../ModuleViewModeToggle";
import { PurchaseDialog } from "../PurchaseDialog";

type Props = {
	sections: HomeSection[];
	exams?: PracticeTestExam[];
	headerData?: HeaderSectionData;
	quickActionsChips?: QuickActionsChipType[];
	handleSelectTopic: (topicId: string) => void;
	handleShowPurchase: () => void;
	recommendationItems?: DiagnosticCardProps[];
	studyPackSubtitle?: string;
	showPurchaseDialog?: boolean;
	toggleShowPurchaseDialog?: () => void;
	hasAccess?: boolean;
};

export const HomeView = ({
	sections,
	headerData,
	handleSelectTopic,
	handleShowPurchase,
	recommendationItems,
	studyPackSubtitle,
	showPurchaseDialog,
	toggleShowPurchaseDialog,
	hasAccess,
	exams,
}: Props) => {
	const { isMobile } = useDevice();
	const theme = useAppTheme();
	const demoExamId = useDemoExamStore((state) => state.currentExam.id);
	const scrollContainerRef = useRef<ScrollView>(null);
	const [moduleViewMode, setModuleViewMode] = useState<ModuleViewMode>("list");

	useEffect(() => {
		scrollContainerRef.current?.scrollTo({ y: 0, animated: true });
	}, []);

	const {
		currentExamContext: { studyPackProductId },
	} = useExamContext();

	const showStudyPackViews = getShowStudyPackViews(studyPackProductId);
	const [showExamsFilterDialog, setShowExamsFilterDialog] = useState(false);

	const toggleShowExamsFilterDialog = () => {
		setShowExamsFilterDialog((prev) => !prev);
	};

	const listHeaderComponent = useMemo(
		() => (
			<ExamInfoSection
				headerData={headerData}
				exams={exams}
				hasAccess={hasAccess}
				handleShowPurchase={handleShowPurchase}
				recommendationItems={recommendationItems}
			/>
		),
		[headerData, exams, hasAccess, handleShowPurchase, recommendationItems],
	);

	const renderItem = ({
		item,
		section,
	}: {
		item: SectionData;
		section: {
			onPress?: () => void;
			loading?: boolean;
		};
	}) => {
		const { type, items } = item;

		if (type === "tasks") {
			return <TasksSection items={items} loading={section.loading} />;
		}

		if (type === "module-recommendations") {
			return (
				<ModuleRecommendationsSection
					items={items as TaskCardProps[]}
					loading={section.loading}
					onPurchasePress={handleShowPurchase}
				/>
			);
		}
		if (type === "progress") {
			return (
				<TopicsSection
					items={items}
					showStudyPackViews={showStudyPackViews}
					handleSelectTopic={handleSelectTopic}
					handleShowPurchase={handleShowPurchase}
					studyPackSubtitle={studyPackSubtitle}
					handleNavigateToDetailView={section.onPress}
					loading={section.loading}
					viewMode={moduleViewMode}
				/>
			);
		}

		if (type === "exam") {
			return <ProductsSection items={items} loading={section.loading} />;
		}
		return null;
	};

	return (
		<ScrollContainer
			ref={scrollContainerRef}
			style={{
				backgroundColor: theme.colors.background,
			}}
			contentContainerStyle={{
				flexGrow: 1,
				paddingBottom: 84,
				paddingTop: isMobile ? 0 : 24,
				paddingHorizontal: isMobile ? 0 : 16,
			}}
			maxWidth="sm"
		>
			<Box style={{ padding: isMobile ? 0 : 8, gap: 24 }}>
				{/* 1. Hero Section (Exam Info / Quick Study) */}
				{listHeaderComponent}

				{/* 2. Recommendation and Module Sections */}
				{sections.length > 0 &&
					sections.map((section) => {
						const sectionItem = section.data?.[0];
						if (!sectionItem) {
							return null;
						}

						return (
							<Box key={section.title} style={{ gap: 12 }}>
								<SectionHeader
									sectionTitle={section.title}
									chipProps={section.chipProps}
									handleClickChips={hasAccess ? undefined : handleShowPurchase}
									rightElement={
										demoExamId === "proethica" &&
										sectionItem.type === "progress" ? (
											<ModuleViewModeToggle
												value={moduleViewMode}
												onValueChange={setModuleViewMode}
											/>
										) : undefined
									}
								/>
								{renderItem({
									item: sectionItem,
									section,
								})}
							</Box>
						);
					})}
			</Box>

			{showExamsFilterDialog && (
				<ExamsFilterDialog
					visible={showExamsFilterDialog}
					handleClose={toggleShowExamsFilterDialog}
				/>
			)}
			{showPurchaseDialog && (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={toggleShowPurchaseDialog!}
					productType="PRACTICE_TEST"
					fromHomePage
				/>
			)}
		</ScrollContainer>
	);
};
